import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  updateProfile,
  sendEmailVerification,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, analytics, functions } from '../../../firebase';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import InfoTooltip from '../../../components/InfoTooltip';
import PasswordRequirements from '../../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../../hooks/auth/usePasswordValidation';
import { setupConfig } from '../2_dashboard/config/providerAccountSetupConfig';
import { getBackgroundGradient } from '../../../utils/environmentTheme';
import { ASSESSMENT_TYPES_BY_ORG } from '../../../constants/assessmentTypesByOrg';

/**
 * Simple, consistent "required field" marker for this screen.
 *
 * Why we do it this way:
 * - UX: we only visually mark mandatory fields (red asterisk) and keep optional labels clean.
 * - Accessibility: the asterisk is marked as decorative, and we add a screen-reader hint.
 * - Maintainability: avoids hardcoding "(optional)" / "(if applicable)" across many labels.
 */
function RequiredFieldMarker() {
  return (
    <>
      <span className="text-red-600" aria-hidden="true">
        {' '}
        *
      </span>
      <span className="sr-only"> (required)</span>
    </>
  );
}

/**
 * Provider "User Type" modelling (important context for future maintainers)
 * -----------------------------------------------------------------------
 * This sign-up screen creates:
 * - a brand new organisation, and
 * - the *first* user for that organisation (typically an Org. Admin).
 *
 * Elsewhere in the product (`ProviderAccountSetupForm`) we model Org. Admin as two UI variants:
 * - "Clinical Org. Admin"      => should be assignable as a clinician
 * - "Non-Clinical Org. Admin"  => must NOT be assignable as a clinician
 *
 * Why we do this:
 * - Backend authorization rules and permissions are based on `userType` values that are deeply embedded:
 *     Clinician | Support | Org. Admin
 * - We *cannot* easily introduce a new backend userType for non-clinical admins without a major refactor.
 * - So we keep backend `userType` as "Org. Admin" for both variants and carry the distinction via:
 *     `isAssignableClinician: boolean`
 *
 * Today, the Cloud Function that powers this screen (`createProviderAccount`) still creates an
 * Org. Admin regardless of what the client sends. We still capture the richer UI intent here and
 * pass it through in the payload so the backend can be upgraded later without reworking the UI again.
 */
const UI_USER_TYPES = Object.freeze({
  CLINICIAN: 'Clinician',
  SUPPORT: 'Support',
  CLINICAL_ORG_ADMIN: 'Clinical Org. Admin',
  NON_CLINICAL_ORG_ADMIN: 'Non-Clinical Org. Admin',
});

function mapUiUserTypeToBackend(uiUserType) {
  switch (uiUserType) {
    case UI_USER_TYPES.CLINICIAN:
      return { userType: 'Clinician', isAssignableClinician: true };
    case UI_USER_TYPES.SUPPORT:
      return { userType: 'Support', isAssignableClinician: false };
    case UI_USER_TYPES.CLINICAL_ORG_ADMIN:
      return { userType: 'Org. Admin', isAssignableClinician: true };
    case UI_USER_TYPES.NON_CLINICAL_ORG_ADMIN:
      return { userType: 'Org. Admin', isAssignableClinician: false };
    default:
      // Fail safe: do not guess. If we ever get an unknown value, treat it as invalid.
      return { userType: null, isAssignableClinician: null };
  }
}

function shouldSkipProfessionalRegistration(uiUserType) {
  // Business rule (shared with the dashboard setup wizard):
  // - Support users and Non-Clinical Org Admins are not clinical staff.
  // - Therefore they do not need professional registration details.
  return uiUserType === UI_USER_TYPES.SUPPORT || uiUserType === UI_USER_TYPES.NON_CLINICAL_ORG_ADMIN;
}

/**
 * Convert a human org name into a stable orgId (hash-based).
 *
 * Why:
 * - Today, a lot of org-specific logic branches on string names like "Phoenix Mental Health Services".
 * - That approach is fragile (typos, inconsistent spacing/casing), and makes migrations painful.
 * - A stable `orgId` lets us:
 *   - key Firestore docs as `organisations/{orgId}`
 *   - keep `displayName` purely as branding/copy
 *   - support aliases for backwards compatibility when reading legacy data
 *
 * Why hash (instead of a user-typed slug):
 * - This sign-up screen is an internal bootstrap tool; the goal is speed and correctness.
 * - Hashing avoids human error, and guarantees we can recompute the same orgId later from the same name.
 * - We keep the ID short for readability, but derived from SHA-256 for collision resistance.
 *
 * Important:
 * - This is deterministic, not secret. Do NOT use this as a security boundary.
 */
function normaliseOrganisationNameForId(value) {
  // Normalisation rules must be stable over time:
  // - trim whitespace
  // - collapse internal whitespace
  // - lowercase
  // - replace & with "and" to avoid accidental mismatches
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/&/g, 'and')
    .toLowerCase();
}

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function hashOrganisationNameToOrgId(organisationName) {
  const normalised = normaliseOrganisationNameForId(organisationName);
  if (!normalised) return '';

  // Prefer Web Crypto (available in modern browsers).
  // Fallback: if crypto.subtle is unavailable, we still return a deterministic slug-like value.
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
      const data = new TextEncoder().encode(normalised);
      const digest = await crypto.subtle.digest('SHA-256', data);
      const hex = bytesToHex(new Uint8Array(digest));
      // Keep short but collision-resistant for our scale.
      // 12 hex chars = 48 bits of entropy (extremely low collision probability for our use-case).
      return `org_${hex.slice(0, 12)}`;
    }
  } catch (_) {
    // Fall through to non-crypto fallback below.
  }

  // Extremely defensive fallback (should be rare):
  // - Deterministic
  // - Not collision-resistant at large scale, but better than blocking sign-up.
  const fallback = normalised
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  return fallback ? `org_${fallback}` : '';
}

/**
 * Org workflow presets
 * -------------------
 * These presets are the scalable alternative to "a thousand booleans".
 *
 * Model:
 * - `preset` is the default behaviour bundle (what most orgs should use).
 * - The org Firestore doc should store only:
 *   - which preset applies
 *   - small overrides (deltas) when an org deviates from the preset
 *
 * Note:
 * - This sign-up screen is an internal bootstrap tool. We collect these values now and include
 *   a generated "org config draft" in the payload. The backend can start persisting it to
 *   `organisations/{orgId}` later without rewriting this UI again.
 */
const ORG_WORKFLOW_PRESETS = Object.freeze({
  default: {
    label: 'Default (CI → SSI → DD → PAD)',
    providerAuthMode: 'hybrid',
    moduleOrder: ['clinicalInterview', 'semiStructuredInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
    qbMode: 'none', // none | qbcheck | qbtest
    skipRecommendedDiagnosis: false,
    ddMode: 'singleClinician', // singleClinician | mdtReview
    mdtSupportingCliniciansRequiredCount: 0,
    patientTerminology: null,
  },
  slam_ssi_first: {
    label: 'SLaM / 3 Borough (SSI → CI → DD → PAD) + Microsoft SSO',
    providerAuthMode: 'with_microsoft_sso',
    moduleOrder: ['semiStructuredInterview', 'clinicalInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
    qbMode: 'none',
    skipRecommendedDiagnosis: false,
    ddMode: 'singleClinician',
    mdtSupportingCliniciansRequiredCount: 0,
    patientTerminology: { informantPlural: 'Key Contacts', informantSingular: 'Key Contact' },
  },
  wlc_mdt: {
    label: 'Well Life / MDT (MDT DD) + QbTest',
    providerAuthMode: 'hybrid',
    moduleOrder: ['clinicalInterview', 'semiStructuredInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
    qbMode: 'qbtest',
    skipRecommendedDiagnosis: false,
    ddMode: 'mdtReview',
    // Current implementation uses “supportingClinicianIds” and injects at least one clinician in docs.
    mdtSupportingCliniciansRequiredCount: 1,
    patientTerminology: null,
  },
  phoenix: {
    label: 'Phoenix (BUPA + QbCheck + screening/referrals)',
    providerAuthMode: 'hybrid',
    moduleOrder: ['clinicalInterview', 'semiStructuredInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
    qbMode: 'qbcheck',
    skipRecommendedDiagnosis: false,
    ddMode: 'singleClinician',
    mdtSupportingCliniciansRequiredCount: 0,
    patientTerminology: null,
  },
  spectrum_skip_rdd: {
    label: 'Spectrum (skip Recommended Diagnosis step) + QbCheck',
    providerAuthMode: 'hybrid',
    moduleOrder: ['clinicalInterview', 'semiStructuredInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
    qbMode: 'qbcheck',
    skipRecommendedDiagnosis: true,
    ddMode: 'singleClinician',
    mdtSupportingCliniciansRequiredCount: 0,
    patientTerminology: null,
  },
});

const PRODUCT_VARIANTS = Object.freeze([
  { value: 'clinical_assessment', label: 'Clinical assessment (default)' },
  { value: 'subclinical', label: 'Subclinical (ThinkDivergent-style)' },
]);

const QB_MODES = Object.freeze([
  { value: 'none', label: 'None' },
  { value: 'qbcheck', label: 'QbCheck' },
  { value: 'qbtest', label: 'QbTest' },
]);

const PROVIDER_AUTH_MODES = Object.freeze([
  { value: 'hybrid', label: 'Hybrid (email/password)' },
  { value: 'with_microsoft_sso', label: 'Microsoft SSO (invite / tenant-gated)' },
]);

/**
 * Non-default dashboard / table filters (org additions)
 *
 * We model these as filter *definitions* (label, type, source, options), not booleans.
 * The org doc should store only additions/removals/overrides relative to global defaults.
 */
const NON_DEFAULT_PATIENT_TABLE_FILTER_DEFINITIONS = Object.freeze({
  paymentMethod: {
    label: 'Payment method',
    type: 'enum',
    source: 'patient.customFields.paymentMethod',
    options: ['Bupa', 'Self-funded'],
  },
  assessmentType: {
    label: 'Assessment Type',
    type: 'enum',
    source: 'patient.customFields.assessmentType',
    // Default template mirrors Well Life Clinic's contract list (incl. NHS - Kent).
    options: [...ASSESSMENT_TYPES_BY_ORG['Well Life Clinic']],
  },
});

function buildOrganisationConfigDraft(formData) {
  const preset = ORG_WORKFLOW_PRESETS[formData.workflowPreset] || ORG_WORKFLOW_PRESETS.default;

  // Build only the non-default additions to patient table filters.
  const filtersToAdd = (formData.patientTableFiltersAdd || [])
    .map((k) => String(k))
    .filter((k) => Boolean(NON_DEFAULT_PATIENT_TABLE_FILTER_DEFINITIONS[k]));

  const patientTableFilterAddDefs = {};
  filtersToAdd.forEach((key) => {
    patientTableFilterAddDefs[key] = NON_DEFAULT_PATIENT_TABLE_FILTER_DEFINITIONS[key];
  });

  // Store only deltas relative to the preset, so org docs stay small and scalable.
  const overrides = {};

  if (formData.providerAuthMode && formData.providerAuthMode !== preset.providerAuthMode) {
    overrides.auth = { provider: { defaultMode: formData.providerAuthMode } };
  }
  if (formData.qbMode && formData.qbMode !== preset.qbMode) {
    overrides.workflow = { ...(overrides.workflow || {}), qb: { mode: formData.qbMode } };
  }
  if (typeof formData.skipRecommendedDiagnosis === 'boolean' && formData.skipRecommendedDiagnosis !== preset.skipRecommendedDiagnosis) {
    overrides.workflow = { ...(overrides.workflow || {}), ssi: { skipRecommendedDiagnosis: formData.skipRecommendedDiagnosis } };
  }
  if (formData.ddMode && formData.ddMode !== preset.ddMode) {
    overrides.workflow = { ...(overrides.workflow || {}), dd: { ...(overrides.workflow?.dd || {}), mode: formData.ddMode } };
  }
  if (
    typeof formData.mdtSupportingCliniciansRequiredCount === 'number' &&
    formData.mdtSupportingCliniciansRequiredCount !== preset.mdtSupportingCliniciansRequiredCount
  ) {
    overrides.workflow = {
      ...(overrides.workflow || {}),
      dd: {
        ...(overrides.workflow?.dd || {}),
        mdtReview: {
          ...(overrides.workflow?.dd?.mdtReview || {}),
          supportingClinicians: { requiredCount: formData.mdtSupportingCliniciansRequiredCount },
        },
      },
    };
  }

  if (formData.patientTerminologyInformantPlural && formData.patientTerminologyInformantPlural !== (preset.patientTerminology?.informantPlural || '')) {
    overrides.terminology = {
      patient: {
        informant: {
          plural: formData.patientTerminologyInformantPlural,
        },
      },
    };
  }

  if (Object.keys(patientTableFilterAddDefs).length > 0) {
    overrides.ui = {
      patientTable: {
        filters: {
          add: patientTableFilterAddDefs,
          // We intentionally do NOT store defaults. The frontend merges this into its default filter set.
          orderAppend: filtersToAdd,
        },
      },
    };
  }

  return {
    orgId: formData.organisationId,
    displayName: formData.organisation,
    productVariant: formData.productVariant,
    configSchemaVersion: 1,
    preset: {
      workflow: formData.workflowPreset,
    },
    // Note: store `overrides` only when non-empty to keep docs small.
    ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
  };
}

export default function ProviderSignUp() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Temporary kill-switch: we’ll iterate on org config capture later.
  // Keep the code in place, but hide the panel so the sign-up UX stays focused.
  const SHOW_ORG_CONFIG_PANEL = false;

  /**
   * We intentionally re-use the same "Title" options as the provider account setup wizard.
   *
   * Why:
   * - This sign-up screen is effectively an internal "org bootstrap" tool for you.
   * - Keeping dropdown options in one shared config avoids subtle drift (e.g. one screen says "Ms." and another doesn't),
   *   and reduces the risk of "data cleanliness" problems in Firestore later.
   *
   * Note:
   * - The backend Cloud Function `createProviderAccount` already supports an optional `title` field
   *   and treats "N/A" as equivalent to empty.
   */
  const titleOptions =
    setupConfig?.default?.steps?.['1']?.fields?.['1']?.options ||
    [];

  const userTypeOptionsRaw =
    setupConfig?.default?.steps?.['2']?.fields?.['1']?.options ||
    [];

  // This screen is for creating the *first* user of a brand-new organisation.
  // In practice, that first user should always be an Org. Admin, so we only
  // show the two Org. Admin variants from the shared config.
  const userTypeOptions = userTypeOptionsRaw.filter(opt =>
    opt?.value === UI_USER_TYPES.CLINICAL_ORG_ADMIN ||
    opt?.value === UI_USER_TYPES.NON_CLINICAL_ORG_ADMIN
  );

  const registrationBodyOptionsRaw =
    setupConfig?.default?.steps?.['3']?.fields?.['1']?.options ||
    [];

  const [formData, setFormData] = useState({
    // Default to "N/A" so the required Title field is frictionless, but still explicit.
    // The backend normalizes this to an empty title.
    title: 'N/A',
    firstName: '',
    lastName: '',
    organisation: '',
    // Stable Firestore id for `organisations/{orgId}` (auto-suggested from organisation name)
    organisationId: '',
    productVariant: 'clinical_assessment',
    workflowPreset: 'default',
    providerAuthMode: ORG_WORKFLOW_PRESETS.default.providerAuthMode,
    qbMode: ORG_WORKFLOW_PRESETS.default.qbMode,
    skipRecommendedDiagnosis: ORG_WORKFLOW_PRESETS.default.skipRecommendedDiagnosis,
    ddMode: ORG_WORKFLOW_PRESETS.default.ddMode,
    mdtSupportingCliniciansRequiredCount: ORG_WORKFLOW_PRESETS.default.mdtSupportingCliniciansRequiredCount,
    // Only needed for orgs that prefer different wording (e.g. SLaM → “Key Contacts”).
    patientTerminologyInformantPlural: '',
    // Org-only additions to default PatientTable filters (we store only additions, not defaults).
    patientTableFiltersAdd: [],
    email: '',
    // Default to Clinical Org Admin since this is usually the "bootstrap admin" account.
    userType: UI_USER_TYPES.CLINICAL_ORG_ADMIN,
    // Optional fields (still collected so org onboarding is faster for you)
    role: '',
    registrationBody: 'N/A',
    registrationNumber: '',
    password: '',
    confirmPassword: ''
  });

  /**
   * Organisation ID auto-generation (hash-based)
   * -------------------------------------------
   * We derive orgId from the organisation display name automatically, so you never have to
   * manually type or maintain slugs.
   *
   * Notes:
   * - This is async because Web Crypto hashing is async.
   * - We guard against race conditions by tracking the latest request.
   */
  useEffect(() => {
    // While the org config panel is hidden, we don't need an orgId yet.
    // Avoid doing async hashing work (and avoid blocking sign-up).
    if (!SHOW_ORG_CONFIG_PANEL) return;

    let isCurrent = true;

    const run = async () => {
      const orgName = formData.organisation;
      const nextId = await hashOrganisationNameToOrgId(orgName);
      if (!isCurrent) return;
      setFormData((prev) => ({ ...prev, organisationId: nextId }));
    };

    run();
    return () => {
      isCurrent = false;
    };
  }, [SHOW_ORG_CONFIG_PANEL, formData.organisation]);

  // If you switch workflow presets, we seed related fields with the preset defaults.
  // You can still override individual values after selecting a preset.
  useEffect(() => {
    const preset = ORG_WORKFLOW_PRESETS[formData.workflowPreset] || ORG_WORKFLOW_PRESETS.default;
    setFormData((prev) => ({
      ...prev,
      providerAuthMode: preset.providerAuthMode,
      qbMode: preset.qbMode,
      skipRecommendedDiagnosis: preset.skipRecommendedDiagnosis,
      ddMode: preset.ddMode,
      mdtSupportingCliniciansRequiredCount: preset.mdtSupportingCliniciansRequiredCount,
      patientTerminologyInformantPlural: preset.patientTerminology?.informantPlural || '',
      // Also seed obvious non-default filter adds for known presets (you can still change manually).
      patientTableFiltersAdd:
        prev.workflowPreset === formData.workflowPreset
          ? prev.patientTableFiltersAdd
          : (formData.workflowPreset === 'phoenix'
              ? ['paymentMethod']
              : formData.workflowPreset === 'wlc_mdt'
                ? ['assessmentType']
                : []),
    }));
  }, [formData.workflowPreset]);

  const organisationConfigDraft = useMemo(() => {
    try {
      if (!SHOW_ORG_CONFIG_PANEL) return null;
      // Only produce a draft when the required org identity fields exist.
      if (!formData.organisationId || !formData.organisation) return null;
      return buildOrganisationConfigDraft(formData);
    } catch {
      return null;
    }
  }, [SHOW_ORG_CONFIG_PANEL, formData]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Keep the form internally consistent:
  // If the chosen user type is NOT a clinical staff member, we clear registration details so
  // we don't accidentally submit irrelevant data (and the UI stays tidy).
  useEffect(() => {
    if (shouldSkipProfessionalRegistration(formData.userType)) {
      setFormData(prev => ({
        ...prev,
        registrationBody: 'N/A',
        registrationNumber: '',
      }));
    }
  }, [formData.userType]);

  // Success banner from URL params (re-using pattern for consistency)
  const [successMessage, setSuccessMessage] = useState('');
  useEffect(() => {
    const message = searchParams.get('message');
    const urlEmail = searchParams.get('email');
    if (message === 'setup_complete') {
      setSuccessMessage('Your account has been set up successfully! Please sign in with your new password.');
      if (urlEmail) setFormData(prev => ({ ...prev, email: decodeURIComponent(urlEmail) }));
    } else if (message === 'account_already_setup') {
      setSuccessMessage('Your account is already set up. Please sign in with your password.');
    }
  }, [searchParams]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    const { errors } = validatePasswordString(newPassword);
    setFormData(prev => ({ ...prev, password: newPassword }));
    if (errors.length > 0) {
      setPasswordError('min. 12 characters inc. at least 1 number, 1 symbol and both upper & lowercase letters');
      setFormData(prev => ({ ...prev, password: newPassword, confirmPassword: '' }));
      setPasswordMatch(null);
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setFormData(prev => ({ ...prev, confirmPassword: newConfirmPassword }));
    setPasswordMatch(newConfirmPassword === formData.password);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const { errors: passwordErrors } = validatePasswordString(formData.password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('. '));
      setIsLoading(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      await updateProfile(userCredential.user, { displayName: `${formData.firstName} ${formData.lastName}` });
      await sendEmailVerification(userCredential.user);
      logEvent(analytics, 'verification_email_sent');

      // Map the UI user type to the backend shape (see header comment above).
      const mappedUserType = mapUiUserTypeToBackend(formData.userType);
      if (!mappedUserType.userType || typeof mappedUserType.isAssignableClinician !== 'boolean') {
        setError('Please choose a valid User Type.');
        setIsLoading(false);
        return;
      }

      const clinicianData = {
        clinicianId: userCredential.user.uid,
        // Optional; backend accepts this and will treat "N/A" as empty.
        title: formData.title,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email.toLowerCase(),
        organisation: formData.organisation,
        // Forward-looking fields (safe today; backend currently ignores extras):
        // - `uiUserType` preserves the UI selection as-is for debugging/telemetry.
        // - `userType` + `isAssignableClinician` is the backend-normalized representation.
        uiUserType: formData.userType,
        userType: mappedUserType.userType,
        isAssignableClinician: mappedUserType.isAssignableClinician,
        // Optional metadata you often know at org creation time.
        // This matches the shape used elsewhere: optional values grouped under `customFields`.
        customFields: {
          role: formData.role || '',
          registrationBody: formData.registrationBody || 'N/A',
          registrationNumber: formData.registrationNumber || '',
        },
        // Organisation config draft (backend can persist to `organisations/{orgId}` later).
        organisationConfigDraft: SHOW_ORG_CONFIG_PANEL ? (organisationConfigDraft || null) : null,
      };

      // Backend callable that finalises the "new organisation bootstrap" by:
      // - setting Provider + Org Admin claims for this newly-created auth user
      // - creating the initial clinicians/{uid} document (seed org admin)
      const createNewOrg = httpsCallable(functions, 'createNewOrg');
      try {
        await createNewOrg(clinicianData);
        navigate('/verify-email');
      } catch (cfError) {
        // If org exists or other CF error, delete the user to avoid orphaned auth accounts
        const isAlreadyExists =
          cfError?.code === 'functions/already-exists' ||
          cfError?.details?.code === 'already-exists' ||
          cfError?.message?.includes('Organisation already exists');

        if (isAlreadyExists) {
          await userCredential.user.delete();
          setError('Organisation already exists. Please contact your Organisation Admin to set up your account.');
          return;
        }
        await userCredential.user.delete();
        setError('An error occurred during sign up. Please try again.');
        throw cfError;
      }
    } catch (err) {
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered. Please sign in instead.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please choose a stronger password.');
          break;
        default:
          setError('An error occurred during sign up. Please try again.');
      }
      logEvent(analytics, 'sign_up_error', { error: err.code });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex justify-center py-10 px-4 overflow-y-auto"
      style={{ backgroundImage: getBackgroundGradient() }}
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl drop-shadow-lg px-8 pb-8">
        <div className="w-full">
          {(import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.VITE_ENVIRONMENT === 'staging') && (
            <div className={`-mx-8 mb-10 px-8 py-3 border font-semibold text-center rounded-t-2xl ${import.meta.env.VITE_ENVIRONMENT === 'development' ? 'bg-yellow-200 text-yellow-900 border-yellow-300' : 'bg-blue-200 text-blue-900 border-blue-300'}`}>
              <div className="flex items-center justify-center gap-2">
                <span>{import.meta.env.VITE_ENVIRONMENT === 'development' ? 'Development Environment' : 'Testing / Training Environment'}</span>
              </div>
            </div>
          )}

          <img src="/td_logo_full_2025.png" alt="Logo" className="mx-auto mb-8" style={{ width: '220px', height: 'auto' }} />
          <h2 className="text-2xl font-bold mb-2 text-center">Set Up New Org.</h2>          

          <form onSubmit={handleSignUp} 
          // autoComplete="on"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
              <div className="md:col-span-3">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                  Title
                  <RequiredFieldMarker />
                </label>
                <select
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
                >
                  {/* Keep placeholder-style empty option for accessibility; required + default value means it's rarely used. */}
                  <option value="" disabled>Select a title</option>
                  {titleOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-5">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                  First Name
                  <RequiredFieldMarker />
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  placeholder="Enter your first name"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                />
              </div>
              <div className="md:col-span-4">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                  Last Name
                  <RequiredFieldMarker />
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  placeholder="Enter your last name"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                Organisation
                <RequiredFieldMarker />
              </label>
              <input
                type="text"
                value={formData.organisation}
                onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
                required
                placeholder="Enter your organisation"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                User role
                <RequiredFieldMarker />
              </label>
              <select
                value={formData.userType}
                onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                required
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
              >
                <option value="" disabled>Select a user role</option>
                {userTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {SHOW_ORG_CONFIG_PANEL && (
              <>
                {/* ------------------------------------------------------------
                    Organisation configuration (org-driven workflows)
                    ------------------------------------------------------------ */}
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Organisation configuration</h3>
                      <p className="text-xs text-gray-600">
                        This section captures <strong>org-level workflow requirements</strong> so we can deprecate hard-coded org checks.
                        We store a preset plus small overrides (deltas), not “a thousand booleans”.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                        Organisation ID (orgId)
                        <RequiredFieldMarker />
                      </label>
                      <input
                        type="text"
                        value={formData.organisationId}
                        required
                        readOnly
                        placeholder="Generating…"
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm font-mono bg-white"
                      />
                      <p className="mt-1 text-[11px] text-gray-500">
                        Generated from organisation name. Used as Firestore doc ID: <span className="font-mono">organisations/{'{orgId}'}</span>.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                        Product variant
                        <RequiredFieldMarker />
                      </label>
                      <select
                        value={formData.productVariant}
                        onChange={(e) => setFormData({ ...formData, productVariant: e.target.value })}
                        required
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
                      >
                        {PRODUCT_VARIANTS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                        Workflow preset
                        <RequiredFieldMarker />
                      </label>
                      <select
                        value={formData.workflowPreset}
                        onChange={(e) => setFormData({ ...formData, workflowPreset: e.target.value })}
                        required
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
                      >
                        {Object.entries(ORG_WORKFLOW_PRESETS).map(([key, preset]) => (
                          <option key={key} value={key}>{preset.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Provider auth mode</label>
                      <select
                        value={formData.providerAuthMode}
                        onChange={(e) => setFormData({ ...formData, providerAuthMode: e.target.value })}
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
                      >
                        {PROVIDER_AUTH_MODES.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Qb workflow mode</label>
                      <select
                        value={formData.qbMode}
                        onChange={(e) => setFormData({ ...formData, qbMode: e.target.value })}
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
                      >
                        {QB_MODES.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-start gap-2">
                      <input
                        id="skip-rdd"
                        type="checkbox"
                        checked={Boolean(formData.skipRecommendedDiagnosis)}
                        onChange={(e) => setFormData({ ...formData, skipRecommendedDiagnosis: e.target.checked })}
                        className="mt-1"
                      />
                      <div>
                        <label htmlFor="skip-rdd" className="text-xs font-bold text-gray-700 text-left">
                          Skip “Recommended Diagnosis” step (SSI → DD)
                        </label>
                        <p className="text-[11px] text-gray-500">
                          Used by Spectrum’s flow today. Prefer capturing as workflow config rather than org string checks.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:col-span-2">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Diagnostic Decision mode</label>
                        <select
                          value={formData.ddMode}
                          onChange={(e) => setFormData({ ...formData, ddMode: e.target.value })}
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
                        >
                          <option value="singleClinician">Single clinician</option>
                          <option value="mdtReview">MDT review</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1 text-left">MDT: review clinicians required</label>
                        <input
                          type="number"
                          min={0}
                          max={5}
                          step={1}
                          value={formData.mdtSupportingCliniciansRequiredCount}
                          onChange={(e) => setFormData({ ...formData, mdtSupportingCliniciansRequiredCount: Number(e.target.value) })}
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                          disabled={formData.ddMode !== 'mdtReview'}
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Patient terminology override (plural)</label>
                      <input
                        type="text"
                        value={formData.patientTerminologyInformantPlural}
                        onChange={(e) => setFormData({ ...formData, patientTerminologyInformantPlural: e.target.value })}
                        placeholder="e.g. Key Contacts"
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                      />
                      <p className="mt-1 text-[11px] text-gray-500">
                        Example: SLaM prefers “Key Contacts” instead of “Informants”.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-700 mb-2 text-left">
                        Add non-default patient table filters
                      </label>
                      <div className="flex flex-col gap-2">
                        {Object.entries(NON_DEFAULT_PATIENT_TABLE_FILTER_DEFINITIONS).map(([key, def]) => {
                          const checked = (formData.patientTableFiltersAdd || []).includes(key);
                          return (
                            <label key={key} className="flex items-start gap-2 text-sm text-gray-800">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = new Set(formData.patientTableFiltersAdd || []);
                                  if (e.target.checked) next.add(key); else next.delete(key);
                                  setFormData({ ...formData, patientTableFiltersAdd: Array.from(next) });
                                }}
                                className="mt-1"
                              />
                              <span>
                                <span className="font-semibold">{def.label}</span>
                                <span className="ml-2 text-[11px] text-gray-500 font-mono">({def.source})</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                      Generated org config draft (for <span className="font-mono">organisations/{'{orgId}'}</span>)
                    </label>
                    <textarea
                      readOnly
                      value={organisationConfigDraft ? JSON.stringify(organisationConfigDraft, null, 2) : ''}
                      className="w-full h-44 px-3 py-2 border rounded-md bg-white font-mono text-[11px] text-gray-800"
                      placeholder="Fill Organisation + orgId to see the generated draft…"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Role</label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder="E.g. Clinical Lead, Service Manager"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
              />
            </div>

            {!shouldSkipProfessionalRegistration(formData.userType) && (
              <>
                <div className="flex space-x-4 mb-4">
                  <div className="w-1/2">
                    <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Registration Body</label>
                    <select
                      value={formData.registrationBody}
                      onChange={(e) => setFormData({ ...formData, registrationBody: e.target.value })}
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm bg-white"
                    >
                      {registrationBodyOptionsRaw.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="w-1/2">
                    <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Registration Number</label>
                    <input
                      type="text"
                      value={formData.registrationNumber}
                      onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                      placeholder="Enter registration number"
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="mb-4">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                  Email
                  <RequiredFieldMarker />
                </label>
                <InfoTooltip content={"Use the email address your organisation registered for you. If you’re unsure, contact your organisation administrator"} side="right" />
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                // autoComplete="username"
                placeholder="Enter your email"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
              />
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                  Password
                  <RequiredFieldMarker />
                </label>
                <InfoTooltip content={"Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"} side="right" />
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handlePasswordChange}
                  required
                  // autoComplete="new-password"
                  placeholder="Enter your password"
                  className="w-full px-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <span className="relative h-5 w-5">
                    <EyeIcon
                      className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                        showPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                      }`}
                    />
                    <EyeOffIcon
                      className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                        showPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                      }`}
                    />
                  </span>
                </button>
              </div>
              <PasswordRequirements value={formData.password} className="mt-2" />
            </div>

            <div className="mb-4">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                  Confirm Password
                  <RequiredFieldMarker />
                </label>
              </div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                  // autoComplete="new-password"
                  placeholder="Confirm your password"
                  className="w-full px-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                  disabled={formData.password === '' || passwordError !== ''}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <span className="relative h-5 w-5">
                    <EyeIcon
                      className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                        showConfirmPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                      }`}
                    />
                    <EyeOffIcon
                      className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                        showConfirmPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                      }`}
                    />
                  </span>
                </button>
              </div>
              {passwordMatch !== null && (
                <div className="mt-1">
                  <div className="flex items-start text-sm">
                    <span className={`mr-2 font-bold ${passwordMatch ? 'text-green-600' : 'text-gray-500'}`} aria-hidden>
                      {passwordMatch ? '✔' : '✘'}
                    </span>
                    <span className={`${passwordMatch ? 'text-green-700' : 'text-gray-700'}`}>
                      {passwordMatch ? 'Passwords match' : 'Passwords do not match'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2 px-4 rounded-md font-semibold text-white flex items-center justify-center mb-4 ${isLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary hover:bg-primary/90'}`}
            >
              {isLoading ? 'Signing up...' : 'Sign Up'}
            </button>
          </form>

          <div className={`transition-all duration-300 overflow-hidden ${successMessage ? 'max-h-24 mb-4' : 'max-h-0 mb-0'}`}>
            <div className={`bg-green-100 border border-green-400 text-green-700 px-4 py-3 text-sm rounded-lg relative ${successMessage ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} role="alert">
              <div className="flex items-start">
                <svg className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                <span>{successMessage}</span>
              </div>
            </div>
          </div>

          <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24' : 'max-h-0'}`}>
            <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            {(() => {
              const subject = 'Reporting Issue with ThinkDivergent';
              const body = [
                'Please describe what you were doing and what happened:',
                '',
                `Screen: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
                `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
                `Time: ${new Date().toISOString()}`,
                `Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
              ].filter(Boolean).join('\n');
              const href = `mailto:support@thinkdivergent.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
              return (
                <p className="text-xs text-gray-500">
                  Need help? Contact your organisation administrator or <a href={href} className="underline text-primary">report an issue</a>.
                </p>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}


